import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserRoundX } from 'lucide-react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { UserAvatar } from '@/components/UserAvatar'
import { User } from '@/schema'
import { cn, formatFullName } from '@/utils'

interface ReviewerListProps {
  reviewers: User[]
  maxVisible?: number
  greyedOut?: boolean
}

export const ReviewerList = ({
  reviewers,
  maxVisible = 5,
  greyedOut = false,
}: ReviewerListProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()

  if (!reviewers || reviewers.length === 0) {
    return <span>-</span>
  }

  const visibleReviewers = reviewers.slice(0, maxVisible)
  const hiddenCount = reviewers.length - maxVisible

  const renderReviewer = (reviewer: User) => {
    const isDisabled = reviewer.status === 'disabled'
    const fullName = formatFullName(reviewer.first_name, reviewer.last_name)

    // For disabled users, we still show tooltip but with different content/icon
    const displayName = isDisabled ? t('common.deletedUser') : fullName

    const trigger = isDisabled ? (
      <div className="flex items-center justify-center bg-muted rounded-full size-6">
        <UserRoundX className="size-3 text-muted-foreground" />
      </div>
    ) : (
      <UserAvatar user={reviewer} size="size-6" greyedOut={greyedOut} />
    )

    return (
      <Tooltip key={reviewer.id} delayDuration={0}>
        <TooltipTrigger asChild>
          <div className="cursor-pointer">{trigger}</div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex items-center gap-2">
            {!isDisabled && (
              <UserAvatar user={reviewer} size="size-6" greyedOut={greyedOut} />
            )}
            {isDisabled && (
              <UserRoundX className="size-4 text-muted-foreground" />
            )}
            <span
              className={cn(
                'text-sm',
                isDisabled && 'italic text-muted-foreground'
              )}
            >
              {displayName}
            </span>
          </div>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div className="flex items-center gap-1">
      {visibleReviewers.map((reviewer) => renderReviewer(reviewer))}
      {hiddenCount > 0 && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'flex items-center justify-center size-6 rounded-full',
                'border border-solid border-gray-light-300 bg-gray-50 hover:bg-gray-100',
                'text-xs font-medium text-gray-700 cursor-pointer',
                'transition-colors'
              )}
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(!isOpen)
              }}
            >
              +{hiddenCount}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-2"
            align="start"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-1">
              {reviewers.slice(maxVisible).map((reviewer) => {
                const isDisabled = reviewer.status === 'disabled'
                const fullName = formatFullName(
                  reviewer.first_name,
                  reviewer.last_name
                )

                if (isDisabled) {
                  return (
                    <div
                      key={reviewer.id}
                      className="flex items-center gap-2 p-2 rounded hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-center bg-muted rounded-full size-6">
                        <UserRoundX className="size-3 text-muted-foreground" />
                      </div>
                      <span className="text-sm italic text-muted-foreground">
                        {t('common.deletedUser')}
                      </span>
                    </div>
                  )
                }

                return (
                  <div
                    key={reviewer.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-gray-50"
                  >
                    <UserAvatar
                      user={reviewer}
                      size="size-6"
                      greyedOut={greyedOut}
                    />
                    <span className="text-sm">{fullName}</span>
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
